import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { dynamicImageUrl, getReactQueryOptions } from '@_/utils-frontend';
import { publicApiQueryClient } from '#src/api.ts';
import { SeoTags } from '#src/components/SeoTags/index.ts';
import { getRequestOnServer } from '#src/functions/getRequestOnServer.ts';
import { ArticlePage } from '#src/pages/ArticlePage/index.ts';
import { ssrWhenNotAuthenticated } from '#src/utils/tanstackRouterUtil.ts';

export const Route = createFileRoute('/{-$locale}/article_/$postId')({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { queryClient } = context;
    const { locale, postId } = params;
    const request = getRequestOnServer();
    await queryClient.ensureQueryData(postQueryOptions({ locale, request, postId }));
  },
  ssr: ssrWhenNotAuthenticated
});

function RouteComponent() {
  const { locale, postId } = Route.useParams();
  const intl = useIntl();

  const query = useSuspenseQuery(postQueryOptions({ locale, postId }));
  const post = query.data.data;

  return (
    <>
      <SeoTags
        description={post.contentPreviewText}
        imageUrl={
          post.images?.length
            ? dynamicImageUrl(post.images[0].imageUrl, { width: 1200 })
            : undefined
        }
        title={intl.formatMessage(
          {
            id: 'head.article-page',
            defaultMessage: '{title} - {author}'
          },
          {
            title: post.title,
            author: post.author.name
          }
        )}
        useCurrentCanonicalPath
      />

      <ArticlePage post={post} />
    </>
  );
}

function postQueryOptions({
  locale,
  request,
  postId
}: {
  locale?: string;
  request?: Request;
  postId: string;
}) {
  return getReactQueryOptions(
    publicApiQueryClient.posts.get,
    { params: { id: postId } },
    { context: { locale, request } }
  );
}
